import * as React from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { Provider } from 'react-redux';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from 'react-router-dom';
import FadeIn from 'react-fade-in';
import 'normalize.css';

import store from 'store';
import { lightTheme, darkTheme } from 'theme';

import useSelector from 'common/hooks/useSelector';
import useDispatch from 'common/hooks/useDispatch';
// import { Helmet } from 'react-helmet';

import Login from 'features/login/login';
import NavBar from 'features/nav/nav';
import AgentHome from 'features/home/agentHome/agentHome';
import AgentBanking from 'features/banking/agentBanking/agentBanking';
import CustomerHome from 'features/home/customerHome/customerHome';
import CustomerBanking from 'features/banking/customerBanking/customerBanking';
import AgentBetting from 'features/betting/agentBetting/agentBetting';
import Reconcile from 'features/reconcile/rencocile';
import MollyWL from 'features/mollywl/mollywl';
import calculatePermissions from 'common/utils/calculatePermissions';
import { CustomerBetting } from 'features/betting/customerBetting/customerBetting';
import {
  doGetUserClients,
  doGetUserWallets,
} from 'common/state/banking/thunkActions';
import { useState } from 'react';
import { loadConfig } from 'common/state/config/thunkActions';
import { Customer } from 'features/customer/customer';
import { doInitSession } from 'common/state/auth/thunkActions';
import { Scrapers } from 'features/scrapers/scrapers';
import { Provider as ProviderPage } from 'features/provider/provider';
import { Movements } from 'features/movements/movements';

const GlobalStyles = createGlobalStyle`
  html, body {
    min-height: 100vh;
    font-family: ${(props) => props.theme.fonts.base};
    font-size: 12pt;
    color: ${(props) => props.theme.foreground};
    background-color: ${(props) => props.theme.background};
  }

  h2 {
    font-size: 14pt;
    font-weight: medium;
  }

  * {
    scrollbar-width: auto;
    scrollbar-color: gray transparent;

  }

  *::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
  *::-webkit-scrollbar-thumb {
    background-color: gray;
    border-radius: 20px;
    }

  .tippy-tooltip.notifications-theme .tippy-backdrop {
    background-color: ${(props) => props.theme.cardLevel2};
    box-shadow: ${(props) => props.theme.shadows[3]};
  }
  .tippy-tooltip.notifications-theme .tippy-arrow {
    color: ${(props) => props.theme.cardLevel2};
    box-shadow: ${(props) => props.theme.shadows[3]};
  }
`;

const LoadingKeyFrames = ({ theme }) => keyframes`
  0% {
    color: ${theme.cardLevel1};
  }
  50% {
    color: ${theme.cardLevel2};
  }
  100% {
    color: ${theme.cardLevel1};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  font-size: 5rem;
  box-sizing: border-box;
  padding: 10%;
  color: ${({ theme }) => theme.cardLevel1};
  animation: 2.5s infinite ${LoadingKeyFrames};
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  font-size: 3rem;
  box-sizing: border-box;
  padding: 10%;
  color: ${({ theme }) => theme.danger};
`;

const AuthenticatedContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const AppContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const ScrollContainer = styled.div`
  display: flex;
  flex: 1;

  overflow: auto;
`;

/**
 * The wrapper app that switches between Authenticated and Unauthenticated apps
 * depending on state
 *
 * Includes Router and Theme
 */
function App() {
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const { auth, theme, clients, wallets, config } = useSelector(
    (state) => state
  );
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(loadConfig()).then(() => setIsConfigLoaded(true));
  }, [dispatch]);
  React.useEffect(() => {
    if (isConfigLoaded && !config.error)
      dispatch(doInitSession()).then(() => setIsCheckingAuth(false));
  }, [dispatch, isConfigLoaded, config.error]);

  React.useEffect(() => {
    if (auth.user) {
      dispatch(doGetUserClients());
      dispatch(doGetUserWallets());
    }
  }, [dispatch, auth.user]);

  return (
    <Router>
      <ThemeProvider theme={theme.theme === 'dark' ? darkTheme : lightTheme}>
        {/* <Helmet>
          <title>Totus</title>
        </Helmet> */}
        <GlobalStyles />
        {!isConfigLoaded ? (
          <LoadingContainer>Loading config...</LoadingContainer>
        ) : config.error ? (
          <ErrorContainer>
            Failed to load config, message someone who knows something...
          </ErrorContainer>
        ) : isCheckingAuth ? (
          <LoadingContainer>Checking user...</LoadingContainer>
        ) : auth.user ? (
          <AuthenticatedContainer>
            {clients && wallets && <AuthenticatedApp />}
          </AuthenticatedContainer>
        ) : (
          <FadeIn>
            <UnauthenticatedApp />
          </FadeIn>
        )}
      </ThemeProvider>
    </Router>
  );
}

/**
 * The app executed when a user is not authenticated with the Totus API
 */
function UnauthenticatedApp() {
  return <Login />;
}

/**
 * The app executed when a user is authenticated with the Totus API
 */
function AuthenticatedApp() {
  const location = useLocation<{ next?: string }>();
  const next = location?.state?.next;

  //eslint-disable-next-line
  // @ts-ignore
  const { groups, impersonator } = useSelector((state) => state.auth.user);

  const permissions = React.useMemo(
    () => calculatePermissions(groups),
    [groups]
  );

  return (
    <AppContainer>
      <NavBar />
      <ScrollContainer>
        <Switch>
          <Route path="/home">
            {permissions.includes('customerHome') ? (
              <CustomerHome />
            ) : permissions.includes('agentHome') ? (
              <AgentHome />
            ) : (
              <ErrorContainer>
                Permissions error, message someone who knows something...
              </ErrorContainer>
            )}
          </Route>
          <Route path="/banking">
            {permissions.includes('customerBanking') ? (
              <CustomerBanking />
            ) : permissions.includes('agentBanking') ? (
              <AgentBanking />
            ) : (
              <Redirect to={next ? next : '/home'} />
            )}
          </Route>
          <Route path="/customers/:username">
            {permissions.includes('customer') ? (
              <Customer />
            ) : (
              <Redirect to={next ? next : '/home'} />
            )}
          </Route>
          <Route path="/providers/:username">
            {permissions.includes('provider') ? (
              <ProviderPage />
            ) : (
              <Redirect to={next ? next : '/home'} />
            )}
          </Route>
          <Route path="/betting">
            {permissions.includes('customerBetting') ? (
              <CustomerBetting />
            ) : permissions.includes('agentBetting') ? (
              <AgentBetting />
            ) : (
              <Redirect to={next ? next : '/home'} />
            )}
          </Route>
          <Route path="/reconcile">
            {permissions.includes('agentReconcile') ? (
              <Reconcile />
            ) : (
              <Redirect to={next ? next : '/home'} />
            )}
          </Route>
          <Route path="/scrapers">
            {permissions.includes('admin') ? (
              <Scrapers />
            ) : (
              <Redirect to={next ? next : '/home'} />
            )}
          </Route>
          <Route path="/movements/:accountCode">
            <Movements />
          </Route>
          <Route path="/mollywl">
            {permissions.includes('mollyWL') ? (
              <MollyWL />
            ) : (
              <Redirect to={next ? next : '/home'} />
            )}
          </Route>
          <Route
            path="/admin"
            component={() => {
              if (permissions.includes('admin') || impersonator) {
                window.location.replace(window.urls.admin);
                return null;
              } else {
                return <Redirect to={next ? next : '/home'} />;
              }
            }}
          ></Route>
          <Route render={() => <Redirect to={next ? next : '/home'} />} />
        </Switch>
      </ScrollContainer>
    </AppContainer>
  );
}

/**
 * Redux store must wrap the App
 */
ReactDOM.render(
  <Provider store={store}>
    <App />
  </Provider>,
  document.getElementById('root')
);
